import { computed, inject, Injectable, signal } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { UploadService } from '@client/upload/services/upload.ws.service';
import { BatchFileSnapshot, BatchSnapshot } from '@client/upload/interfaces/batch-snapshot.interface';
import { StagedFileUploadRequestDto } from '@client/work-packages/interfaces/staged-file.interface';
import { RetryPlan } from '@client/upload/interfaces/retry-plan.interface';
import { ShareRecipient } from '@privanot/shared-models';
import { BatchStatus } from '@client/upload/enums/upload-batch-status.enum';
import { BatchFileStatus } from '@client/upload/enums/upload-batch-file-status.enum';
import { TranslateService } from '@ngx-translate/core';
import { PnToastService } from '@privanot/shared-ui/primeng-acl/toast';
import { ActiveRun } from './active-run';

/** File error the Client Service records when the user's session ended during the run (TTP-192). */
const SESSION_EXPIRED_ERROR = 'session_expired';

const WATCHDOG_INTERVAL_MS = 5000;
const STALL_THRESHOLD_MS = 40000;
const RECOVERY_RETRY_DELAYS_MS = [2000, 5000, 10000, 30000];

@Injectable({ providedIn: 'root' })
export class UploadProgressService {
    private uploadService = inject(UploadService);
    private readonly toastService = inject(PnToastService);
    private readonly translateService = inject(TranslateService);

    private readonly _batch = signal<BatchSnapshot | null>(null);
    public readonly batch = this._batch.asReadonly();

    private readonly _isPanelOpen = signal(false);
    public readonly isPanelOpen = this._isPanelOpen.asReadonly();

    private readonly _stalledFileIds = signal<ReadonlySet<string>>(new Set());
    public readonly stalledFileIds = this._stalledFileIds.asReadonly();

    public readonly isActive = computed(() => this._batch()?.status === BatchStatus.IN_PROGRESS);
    public readonly hasBatch = computed(() => this._batch() !== null);

    public readonly isReconnecting = computed(() => this.uploadService.state() === HubConnectionState.Reconnecting);
    public readonly isConnectionLost = computed(() => this.isActive() && this.uploadService.state() === HubConnectionState.Disconnected);

    public readonly progress = computed(() => {
        const b = this._batch();
        if (!b || b.files.length === 0) return 0;
        const done = b.files.filter((f) => f.status === BatchFileStatus.COMPLETED || f.status === BatchFileStatus.FAILED).length;
        const active = b.files.find((f) => f.status === BatchFileStatus.IN_PROGRESS);
        let fraction = done;
        if (active && active.percent != null) {
            fraction += active.percent / 100;
        }
        return Math.round((fraction / b.files.length) * 100);
    });

    public readonly completedCount = computed(() => this._batch()?.files.filter((f) => f.status === BatchFileStatus.COMPLETED).length ?? 0);
    public readonly failedCount = computed(() => this._batch()?.files.filter((f) => f.status === BatchFileStatus.FAILED).length ?? 0);

    private unsubscribers: (() => void)[] = [];
    private watchdogHandle: ReturnType<typeof setInterval> | null = null;
    private recoveryHandle: ReturnType<typeof setTimeout> | null = null;
    private readonly lastProgressAt = new Map<string, number>();
    private sessionExpiredNoticeFor: string | null = null;

    constructor() {
        inject(ActiveRun).track(() => this.isActive());
    }

    public seedFromSnapshot(snapshot: BatchSnapshot): void {
        this._batch.set(snapshot);
    }

    public fileProgress(file: BatchFileSnapshot): number | null {
        return file.percent ?? null;
    }

    public async startBatch(
        projectId: string,
        name: string,
        files: StagedFileUploadRequestDto[],
        recipients: ShareRecipient[] = [],
    ): Promise<void> {
        await this.runBatch(() => this.uploadService.confirmFileSelection(projectId, name, files, recipients));
    }

    public async planRetry(operationId: string, operationFileIds: string[]): Promise<RetryPlan> {
        await this.uploadService.listen();
        try {
            return await this.uploadService.getRetryPlan(operationId, operationFileIds);
        } finally {
            await this.uploadService.stopListening();
        }
    }

    public async startRetry(
        operationId: string,
        operationFileIds: string[],
        files: StagedFileUploadRequestDto[] | null = null,
    ): Promise<void> {
        await this.runBatch(() => this.uploadService.retryOperationFiles(operationId, operationFileIds, files));
    }

    private async runBatch(start: () => Promise<BatchSnapshot>): Promise<void> {
        if (this.isActive()) {
            throw new Error('An upload batch is already in progress.');
        }

        await this.uploadService.listen();
        this.registerEvents();

        try {
            const snapshot = await start();
            this.seedFromSnapshot(snapshot);
            this._isPanelOpen.set(true);
            if (snapshot.status !== BatchStatus.IN_PROGRESS) {
                this.unregisterEvents();
                await this.uploadService.stopListening();
            }
        } catch (err) {
            this.unregisterEvents();
            await this.uploadService.stopListening();
            throw err;
        }
    }

    public openPanel(): void {
        this._isPanelOpen.set(true);
    }

    public closePanel(): void {
        this._isPanelOpen.set(false);
    }

    public togglePanel(): void {
        this._isPanelOpen.update((v) => !v);
    }

    public async resumeIfActive(snapshot: BatchSnapshot | null): Promise<void> {
        if (!snapshot) return;

        await this.uploadService.listen();
        this.registerEvents();

        try {
            const live = await this.uploadService.subscribeToBatch(snapshot.id);
            this.seedFromSnapshot(live);
            if (live.status !== BatchStatus.IN_PROGRESS) {
                this.unregisterEvents();
                await this.uploadService.stopListening();
            }
        } catch (err) {
            this.unregisterEvents();
            await this.uploadService.stopListening();
            throw err;
        }
    }

    public async dismiss(): Promise<void> {
        const current = this._batch();
        this.unregisterEvents();
        if (current) {
            try {
                await this.uploadService.listen();
                await this.uploadService.dismissUploadBatch(current.id);
            } catch {
                // intentionally empty
            }
        }
        await this.uploadService.stopListening();
        this._batch.set(null);
        this._isPanelOpen.set(false);
    }

    private registerEvents(): void {
        if (this.unsubscribers.length > 0) {
            return;
        }
        this.unsubscribers.push(
            this.uploadService.onFileUploadStarted(({ fileId }) => {
                this.patchFile(fileId, { status: BatchFileStatus.IN_PROGRESS });
                this.noteProgress(fileId);
            }),
            this.uploadService.onFileUploadProgress(({ fileId, phase, rowsProcessed, percent, fileIndex, innerPercent }) => {
                this.patchFile(fileId, { phase, rowsProcessed, percent, fileIndex, innerPercent });
                this.noteProgress(fileId);
            }),
            this.uploadService.onFileUploadCompleted(({ fileId }) => this.patchFile(fileId, { status: BatchFileStatus.COMPLETED })),
            this.uploadService.onFileUploadFailed(({ fileId, error }) => {
                this.patchFile(fileId, { status: BatchFileStatus.FAILED, error });
                if (error === SESSION_EXPIRED_ERROR) {
                    this.noticeSessionExpired();
                }
            }),
            this.uploadService.onBatchCompleted(async ({ status, shareError }) => {
                this._batch.update((b) => (b ? { ...b, status, shareError, completedAt: new Date().toISOString() } : b));
                this.unregisterEvents();
                await this.uploadService.stopListening();
            }),
            this.uploadService.onReconnected(() => void this.resubscribe()),
            this.uploadService.onClosed(() => this.scheduleRecovery(() => this.restartConnection(1), 0)),
        );
        this.startWatchdog();
    }

    /** Once per batch: the remaining files all fail with the same reason. */
    private noticeSessionExpired(): void {
        const batchId = this._batch()?.id ?? null;
        if (batchId === null || this.sessionExpiredNoticeFor === batchId) {
            return;
        }
        this.sessionExpiredNoticeFor = batchId;
        this.toastService.show({
            severity: 'warn',
            summary: this.translateService.instant('header.uploadProgress.sessionExpired.summary'),
            detail: this.translateService.instant('header.uploadProgress.sessionExpired.detail'),
        });
    }

    private unregisterEvents(): void {
        for (const u of this.unsubscribers) u();
        this.unsubscribers = [];
        this.cancelRecovery();
        this.stopWatchdog();
        this.lastProgressAt.clear();
        this._stalledFileIds.set(new Set());
    }

    private async resubscribe(attempt = 0): Promise<void> {
        const current = this._batch();
        if (!current || current.status !== BatchStatus.IN_PROGRESS) {
            return;
        }

        try {
            const live = await this.uploadService.subscribeToBatch(current.id);
            this.mergeSnapshot(live);
            if (live.status !== BatchStatus.IN_PROGRESS) {
                this.unregisterEvents();
                await this.uploadService.stopListening();
            }
        } catch {
            this.scheduleRecovery(() => this.resubscribe(attempt + 1), attempt);
        }
    }

    private async restartConnection(attempt: number): Promise<void> {
        try {
            await this.uploadService.restart();
        } catch {
            this.scheduleRecovery(() => this.restartConnection(attempt + 1), attempt);
        }
    }

    private scheduleRecovery(run: () => Promise<void>, attempt: number): void {
        if (this.unsubscribers.length === 0) {
            return;
        }
        this.cancelRecovery();
        const delay = RECOVERY_RETRY_DELAYS_MS[Math.min(attempt, RECOVERY_RETRY_DELAYS_MS.length - 1)];
        this.recoveryHandle = setTimeout(() => {
            this.recoveryHandle = null;
            void run();
        }, delay);
    }

    private cancelRecovery(): void {
        if (this.recoveryHandle !== null) {
            clearTimeout(this.recoveryHandle);
            this.recoveryHandle = null;
        }
    }

    private mergeSnapshot(live: BatchSnapshot): void {
        this._batch.update((b) => {
            if (!b || b.id !== live.id) return live;
            const local = new Map(b.files.map((f) => [f.id, f]));
            return { ...b, ...live, files: live.files.map((f) => ({ ...local.get(f.id), ...f })) };
        });
        for (const f of live.files) {
            if (f.status === BatchFileStatus.COMPLETED || f.status === BatchFileStatus.FAILED) {
                this.lastProgressAt.delete(f.id);
                this.clearStalled(f.id);
            }
        }
    }

    private startWatchdog(): void {
        if (this.watchdogHandle !== null) {
            return;
        }
        this.watchdogHandle = setInterval(() => this.evaluateStalledFiles(), WATCHDOG_INTERVAL_MS);
    }

    private stopWatchdog(): void {
        if (this.watchdogHandle !== null) {
            clearInterval(this.watchdogHandle);
            this.watchdogHandle = null;
        }
    }

    private noteProgress(fileId: string): void {
        this.lastProgressAt.set(fileId, Date.now());
        this.clearStalled(fileId);
    }

    private evaluateStalledFiles(): void {
        const b = this._batch();
        if (!b) {
            return;
        }
        const now = Date.now();
        const next = new Set<string>();
        for (const f of b.files) {
            if (f.status !== BatchFileStatus.IN_PROGRESS) {
                continue;
            }
            const last = this.lastProgressAt.get(f.id);
            if (last != null && now - last >= STALL_THRESHOLD_MS) {
                next.add(f.id);
            }
        }
        this.setStalled(next);
    }

    private clearStalled(fileId: string): void {
        const current = this._stalledFileIds();
        if (!current.has(fileId)) {
            return;
        }
        const next = new Set(current);
        next.delete(fileId);
        this._stalledFileIds.set(next);
    }

    private setStalled(next: Set<string>): void {
        const current = this._stalledFileIds();
        if (current.size === next.size && [...next].every((id) => current.has(id))) {
            return;
        }
        this._stalledFileIds.set(next);
    }

    private patchFile(fileId: string, patch: Partial<BatchSnapshot['files'][number]>): void {
        if (patch.status === BatchFileStatus.COMPLETED || patch.status === BatchFileStatus.FAILED) {
            this.lastProgressAt.delete(fileId);
            this.clearStalled(fileId);
        }
        this._batch.update((b) => {
            if (!b) return b;
            return {
                ...b,
                files: b.files.map((f) => (f.id === fileId ? { ...f, ...patch } : f)),
            };
        });
    }
}
